import { Auth0Client } from "@auth0/auth0-spa-js";
import axios from "axios";
import { apiConfig, auth0Config } from "../config";
import { Status } from "../slices/options/appointments";
import { Appointment } from "../types/appointment";
import asArray from "../utils/asArray";
import querySearchEndpoint, {
  queryUpdateEndpoint,
} from "../utils/querySearchEndpoint";
import { getAuthConfigWithProps, getBasicAuthConfig } from "./authUtils";
import {
  AppointmentSettings,
  UpdateAppointmentSettingsDto,
  AppointmentReminderOption,
} from "../types/AppointmentSettings";

class AppointmentApi {
  private auth0Client: Auth0Client | null = null;

  constructor() {
    this.auth0Client = new Auth0Client({
      redirect_uri: window.location.origin,
      ...auth0Config,
    });
  }

  async getCurrentAppointment(
    appointmentId: string,
    query: Record<string, any>,
  ) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);

      const url = new URL(
        `${apiConfig.appointmentServiceBase}/appointments/${appointmentId}`,
      );
      const response = await querySearchEndpoint(url, query, config);
      return response;
    } catch (e) {
      console.error(`Error when retrieving appointment ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async getAppointmentMessages(
    appointmentId: string,
    query?: { [key: string]: any },
  ) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);

      const url = new URL(
        `${apiConfig.appointmentServiceBase}/appointments/messages/${appointmentId}`,
      );
      return await querySearchEndpoint(url, query, config);
    } catch (e) {
      console.error(
        `Error retrieving appointment's messages ${JSON.stringify(e)}`,
      );
      throw e;
    }
  }

  async findAppointments(
    query: any,
    signal?: AbortSignal,
  ): Promise<Appointment[]> {
    try {
      if (asArray(query.status, []).length === 0) {
        query.status = Status.filter(
          (e) => !["entered-in-error", "cancelled"].includes(e?.code),
        ).map((e) => e.code);
      }

      const config = await getAuthConfigWithProps(this.auth0Client);
      const url = new URL(`${apiConfig.appointmentServiceBase}/appointments`);

      if (signal) {
        config.signal = signal;
      }

      const response = querySearchEndpoint(url, query, config);
      return await response;
    } catch (e) {
      console.error(`Error when retrieving appointments ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async savedAppointment(appointmentData: Appointment) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.post(
        `${apiConfig.appointmentServiceBase}/appointments`,
        appointmentData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      console.error(`Error when saving appointments ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async updateAppointment(
    appointmentId: string,
    appointmentData: Appointment,
    query?: Record<string, any>,
  ) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);

      const url = new URL(
        `${apiConfig.appointmentServiceBase}/appointments/${appointmentId}`,
      );
      const response = await queryUpdateEndpoint(
        url,
        query,
        config,
        appointmentData,
      );
      return response;
    } catch (e) {
      console.error(`Error when updating appointment ${JSON.stringify(e)}`);
      throw e;
    }
  }
  // count

  async countAppointment(query: any): Promise<number> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(
        `${apiConfig.appointmentServiceBase}/appointments/stats/count`,
      );
      const response = await querySearchEndpoint(url, query, config);
      return response?.count;
    } catch (e) {
      console.error(
        `Error when retrieving total number of appointments ${JSON.stringify(
          e,
        )}`,
      );
      throw e;
    }
  }

  async getPopularHours(organizationId: string, period: string, query: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(
        `${apiConfig.appointmentServiceBase}/appointments/schedule/popular-hours/${organizationId}/${period}`,
      );
      const response = querySearchEndpoint(url, query, config);
      return await response;
    } catch (e) {
      console.error(
        `Error when retrieving total number of appointments ${JSON.stringify(
          e,
        )}`,
      );
      throw e;
    }
  }

  async findHolidays(query) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(`${apiConfig.appointmentServiceBase}/holidays`);
      const response = querySearchEndpoint(url, query, config);
      return await response;
    } catch (e) {
      console.error(`Error when retrieving holidays ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async findBroadcasts(query: any, signal?: AbortSignal) {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );
      const url = new URL(`${apiConfig.appointmentServiceBase}/communications`);
      const response = querySearchEndpoint(url, query, config);
      return await response;
    } catch (e) {
      console.error(`Error when retrieving broadcasts ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async findBroadcastById(broadcastId: string) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.appointmentServiceBase}/communications/${broadcastId}`,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      console.error(`Error when retrieving broadcast ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async saveBroadcast(broadcastData: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.post(
        `${apiConfig.appointmentServiceBase}/communications`,
        broadcastData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      console.error(`Error when saving broadcast ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async updateBroadcast(broadcastId: string, broadcastData: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.put(
        `${apiConfig.appointmentServiceBase}/communications/${broadcastId}`,
        broadcastData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      console.error(`Error when updating broadcast ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async deleteBroadcast(broadcastId: string) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      await axios.delete(
        `${apiConfig.appointmentServiceBase}/communications/${broadcastId}`,
        config,
      );
    } catch (e) {
      console.error(`Error when deleting broadcast ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async findFirstAppointmentForPatient(
    patientId: string,
    organizationId: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    const response = await axios.get(
      `${apiConfig.appointmentServiceBase}/appointments/patients/${patientId}/organizations/${organizationId}/first`,
      config,
    );

    return response.data;
  }

  // Get analytics from scheduling data (general method for all aggregation types)
  async getAnalytics(organizationId: string, query: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(
        `${apiConfig.appointmentServiceBase}/appointments/analytics/${organizationId}`,
      );

      const response = querySearchEndpoint(url, query, config);
      return await response;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving appointment analytics with organizationId: ${organizationId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occurred. Please try again.`);
    }
  }

  // Get new vs returning patient analytics from scheduling data
  async getPatientAnalytics(organizationId: string, query: any) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const url = new URL(
        `${apiConfig.appointmentServiceBase}/appointments/analytics/${organizationId}`,
      );

      const response = querySearchEndpoint(url, query, config);
      return await response;
    } catch (e) {
      if (e?.response?.data?.statusCode === 403) {
        return {};
      }
      console.error(
        `Error when retrieving appointment analytics with organizationId: ${organizationId}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(`An error occurred. Please try again.`);
    }
  }

  // healthCheck
  async healthCheck() {
    try {
      const response = await axios.get(
        `${apiConfig.appointmentServiceBase}/health`,
      );
      return response.data;
    } catch (e) {
      console.error(
        `Appointment Service health check was unsuccessful${JSON.stringify(e)}`,
      );
    }
  }

  // ============== APPOINTMENT SETTINGS METHODS ==============

  // Get appointment settings by ID
  async getAppointmentSettingsById(id: string): Promise<AppointmentSettings> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.appointmentServiceBase}/appointment-settings/id/${id}`,
        config,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting appointment settings by ID:", error);
      throw error;
    }
  }

  // Get appointment settings by organization (creates default if not exists)
  async getAppointmentSettingsByOrganization(
    organizationId: string,
  ): Promise<AppointmentSettings> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.appointmentServiceBase}/appointment-settings/${organizationId}`,
        config,
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error getting appointment settings by organization:",
        error,
      );
      throw error;
    }
  }

  // Update appointment settings by ID
  async updateAppointmentSettingsById(
    id: string,
    settings: UpdateAppointmentSettingsDto,
  ): Promise<AppointmentSettings> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.put(
        `${apiConfig.appointmentServiceBase}/appointment-settings/id/${id}`,
        settings,
        config,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating appointment settings by ID:", error);
      throw error;
    }
  }

  // Update appointment settings by organization
  async updateAppointmentSettingsByOrganization(
    organizationId: string,
    settings: UpdateAppointmentSettingsDto,
  ): Promise<AppointmentSettings> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.put(
        `${apiConfig.appointmentServiceBase}/appointment-settings/${organizationId}`,
        settings,
        config,
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error updating appointment settings by organization:",
        error,
      );
      throw error;
    }
  }

  // Get available reminder types with labels and descriptions
  async getAvailableReminderTypes(): Promise<AppointmentReminderOption[]> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.appointmentServiceBase}/appointment-settings/reminder-types/available`,
        config,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting available reminder types:", error);
      throw error;
    }
  }
}
export const appointmentApi = new AppointmentApi();
